import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import { DirectionProvider } from '@/components/ui/direction';
import { ThemeProvider } from '@/components/theme-provider';
import { registerServiceWorker } from '@/lib/register-sw';
import { feedQueryOptions } from '@/lib/feed-query';
import { queryClient } from '@/lib/query-client';
import { SessionProvider } from '@/lib/session';
import { router } from './router';
import './index.css';

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('#root missing');

/*
 * Radix primitives resolve their own start/end behaviour from this provider
 * rather than from the CSS `dir`, so the two must agree. `dir` is set on <html>
 * before paint by the bootstrap in index.html; read it back rather than
 * duplicating the locale logic.
 */
const direction = document.documentElement.dir === 'rtl' ? 'rtl' : 'ltr';

/*
 * START THE SIGNED-IN FRONT DOOR BESIDE THE SESSION GATE, NOT BEHIND IT.
 *
 * `AppShell` cannot mount `FeedScreen` until `/me` says the account is ready. If
 * the feed begins only then, every cold home load pays two network round trips in
 * series. The feed endpoint deliberately answers an unsigned request with an
 * empty page, so it is safe to ask at boot; a signed-in session gets both reads
 * in parallel, and `FeedScreen` adopts this exact promise from TanStack Query.
 *
 * Password sign-in and onboarding happen without a document reload. `refresh`
 * in `SessionProvider` removes this anonymous/unfinished cache before opening
 * the gate, so an early empty answer can never masquerade as a ready account's
 * feed.
 */
void queryClient.prefetchInfiniteQuery(feedQueryOptions());

/*
 * Before render, and deliberately not awaited: the helper waits for `load` and
 * then browser idle, so installing the offline cache cannot compete with the
 * session and first feed reads. See lib/register-sw.ts — it also bounds that
 * delay so a new deploy still reaches a phone promptly.
 */
registerServiceWorker();

createRoot(rootEl).render(
  <StrictMode>
    <ThemeProvider>
      <DirectionProvider dir={direction}>
        <QueryClientProvider client={queryClient}>
          {/*
            INSIDE THE QUERY CLIENT AND OUTSIDE THE ROUTER, which is the only place
            it can go: the session is a react-query read (`lib/session`), and every
            route is behind it — `AppShell` renders the sign-in screen or onboarding
            instead of the tab shell, so the router must already be able to ask who
            is signed in by the time the first route renders.
          */}
          <SessionProvider>
            <RouterProvider router={router} />
          </SessionProvider>
        </QueryClientProvider>
      </DirectionProvider>
    </ThemeProvider>
  </StrictMode>,
);
